import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Chip
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: "bold",
    color: theme.palette.primary.main
  },
  divider: {
    margin: theme.spacing(2, 0)
  },
  uploadArea: {
    border: "2px dashed #ccc",
    borderRadius: 8,
    padding: theme.spacing(2),
    textAlign: "center",
    cursor: "pointer",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: "#f5f5f5"
    }
  },
  certInfo: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: "#e8f5e9",
    borderRadius: 4
  },
  infoBox: {
    padding: theme.spacing(2),
    backgroundColor: "#e3f2fd",
    borderRadius: 8,
    marginBottom: theme.spacing(2)
  }
}));

const FiscalSettingsModal = ({ open, onClose }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [settings, setSettings] = useState({
    companyName: "",
    companyCnpj: "",
    companyIm: "",
    companyStreet: "",
    companyNumber: "",
    companyComplement: "",
    companyNeighborhood: "",
    companyCity: "Santo André",
    companyState: "SP",
    companyZipCode: "",
    companyCityCode: "3547809",
    defaultServiceCode: "",
    defaultServiceCnae: "",
    defaultServiceMunicipalCode: "",
    defaultServiceNationalCode: "",
    defaultServiceDescription: "Serviço de intermediação de consórcio",
    defaultIssRate: 5.0,
    nuvemFiscalEnvironment: "homologacao",
    certificatePassword: "",
    taxRegime: "simples_nacional"
  });

  useEffect(() => {
    if (open) {
      loadSettings();
    }
  }, [open]);

  const loadSettings = async () => {
    try {
      const { data } = await api.get("/fiscal/settings");
      if (data) {
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (err) {
      // Sem configurações ainda
    }
  };

  const handleChange = (field) => (e) => {
    setSettings(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.put("/fiscal/settings", settings);
      toast.success("Configurações salvas com sucesso!");
      onClose();
    } catch (err) {
      toast.error("Erro ao salvar configurações.");
    }
    setLoading(false);
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/fiscal/settings/test");
      if (data.success) {
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error("Erro ao testar conexão com o Emissor Nacional.");
    }
    setTesting(false);
  };

  const handleCertificateUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith(".pfx") && !file.name.endsWith(".p12")) {
      toast.error("O certificado deve ser um arquivo .pfx ou .p12");
      return;
    }

    const formData = new FormData();
    formData.append("certificate", file);

    try {
      const { data } = await api.post("/fiscal/settings/certificate", formData);
      toast.success("Certificado enviado com sucesso!");
      setSettings(prev => ({ ...prev, certificateFile: data.path }));
    } catch (err) {
      toast.error("Erro ao enviar certificado.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>Configurações Fiscais - Emissor Nacional NFS-e</DialogTitle>
      <DialogContent dividers>
        {/* Info Box */}
        <div className={classes.infoBox}>
          <Typography variant="body2">
            <strong>Emissor Nacional NFS-e (gov.br)</strong> — Sistema gratuito do governo federal para emissão de NFS-e.
            Requer certificado digital A1 (e-CNPJ) no formato .pfx e que o município de Santo André esteja conveniado ao padrão nacional.
          </Typography>
        </div>

        {/* Dados da Empresa */}
        <Typography variant="subtitle1" className={classes.sectionTitle}>
          Dados da Empresa Emissora
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="Razão Social"
              value={settings.companyName}
              onChange={handleChange("companyName")}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="CNPJ"
              value={settings.companyCnpj}
              onChange={handleChange("companyCnpj")}
              variant="outlined"
              size="small"
              placeholder="00.000.000/0000-00"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Inscrição Municipal"
              value={settings.companyIm}
              onChange={handleChange("companyIm")}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Código IBGE Município"
              value={settings.companyCityCode}
              onChange={handleChange("companyCityCode")}
              variant="outlined"
              size="small"
              helperText="Santo André: 3547809"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Regime Tributário</InputLabel>
              <Select
                value={settings.taxRegime}
                onChange={handleChange("taxRegime")}
                label="Regime Tributário"
              >
                <MenuItem value="simples_nacional">Simples Nacional (ME/EPP)</MenuItem>
                <MenuItem value="mei">MEI</MenuItem>
                <MenuItem value="regime_normal">Regime Normal</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Endereço */}
        <Typography variant="subtitle1" className={classes.sectionTitle}>
          Endereço da Empresa
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Logradouro"
              value={settings.companyStreet}
              onChange={handleChange("companyStreet")}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="Número"
              value={settings.companyNumber}
              onChange={handleChange("companyNumber")}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Complemento"
              value={settings.companyComplement}
              onChange={handleChange("companyComplement")}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Bairro"
              value={settings.companyNeighborhood}
              onChange={handleChange("companyNeighborhood")}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Cidade"
              value={settings.companyCity}
              onChange={handleChange("companyCity")}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="UF"
              value={settings.companyState}
              onChange={handleChange("companyState")}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="CEP"
              value={settings.companyZipCode}
              onChange={handleChange("companyZipCode")}
              variant="outlined"
              size="small"
            />
          </Grid>
        </Grid>

        <Divider className={classes.divider} />

        {/* Serviço Padrão */}
        <Typography variant="subtitle1" className={classes.sectionTitle}>
          Serviço Padrão (NFS-e)
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Código Tributação Nacional"
              value={settings.defaultServiceNationalCode}
              onChange={handleChange("defaultServiceNationalCode")}
              variant="outlined"
              size="small"
              helperText="Código NBS/Lista Nacional"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Código Tributação Municipal"
              value={settings.defaultServiceMunicipalCode}
              onChange={handleChange("defaultServiceMunicipalCode")}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="CNAE"
              value={settings.defaultServiceCnae}
              onChange={handleChange("defaultServiceCnae")}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Alíquota ISS (%)"
              value={settings.defaultIssRate}
              onChange={handleChange("defaultIssRate")}
              variant="outlined"
              size="small"
              type="number"
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="Descrição Padrão do Serviço"
              value={settings.defaultServiceDescription}
              onChange={handleChange("defaultServiceDescription")}
              variant="outlined"
              size="small"
              multiline
              rows={2}
            />
          </Grid>
        </Grid>

        <Divider className={classes.divider} />

        {/* Certificado Digital */}
        <Typography variant="subtitle1" className={classes.sectionTitle}>
          Certificado Digital (e-CNPJ A1)
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <input
              accept=".pfx,.p12"
              style={{ display: "none" }}
              id="certificate-upload"
              type="file"
              onChange={handleCertificateUpload}
            />
            <label htmlFor="certificate-upload">
              <div className={classes.uploadArea}>
                <Typography variant="body2" color="textSecondary">
                  {settings.certificateFile 
                    ? `✅ Certificado carregado: ${settings.certificateFile.split("/").pop()}`
                    : "Clique para enviar o certificado digital (.pfx ou .p12)"
                  }
                </Typography>
              </div>
            </label>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Senha do Certificado"
              value={settings.certificatePassword}
              onChange={handleChange("certificatePassword")}
              variant="outlined"
              size="small"
              type="password"
            />
          </Grid>
        </Grid>

        <Divider className={classes.divider} />

        {/* Ambiente */}
        <Typography variant="subtitle1" className={classes.sectionTitle}>
          Ambiente de Emissão
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Ambiente</InputLabel>
              <Select
                value={settings.nuvemFiscalEnvironment}
                onChange={handleChange("nuvemFiscalEnvironment")}
                label="Ambiente"
              >
                <MenuItem value="homologacao">
                  Homologação (Testes - Produção Restrita)
                </MenuItem>
                <MenuItem value="producao">
                  Produção (Notas reais)
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleTestConnection}
              disabled={testing}
              fullWidth
              style={{ height: 40 }}
            >
              {testing ? <CircularProgress size={20} /> : "Testar Conexão"}
            </Button>
          </Grid>
        </Grid>

      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="default">
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={loading}
        >
          {loading ? <CircularProgress size={20} /> : "Salvar Configurações"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FiscalSettingsModal;
